import { getDistance as haversineDist } from 'ol/sphere';
import { isPilotPointName, legForEndPoint, pruneRouteGeometry } from './helpers';
import { VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { getLegTypeFromDist } from '../utils/map-utils';
import { Format, ImportResult } from './types/converters.types';
import { XMLParser } from 'fast-xml-parser';

const NMToMeters = 1852; // Nautical miles -> meters

// SPOS 9 XML output doesn't contain info on RL vs GC on segments. We assume GC on segments > 300nm

export class SposConverter {
    static check(str: string) {
        try {
            const parser = new XMLParser({
                ignoreAttributes: false,
                attributeNamePrefix: '',
            });
            const parsed = parser.parse(str);

            const root = parsed && parsed.sposroutetemplate ? parsed.sposroutetemplate : null;

            if (root) {
                return parsed;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const parsed = SposConverter.check(str) as any;
            if (!parsed || !parsed.sposroutetemplate) return;

            let waypoints: any = parsed.sposroutetemplate.template?.waypoints?.waypoint;
            if (!waypoints) {
                return;
            }
            if (!Array.isArray(waypoints)) {
                waypoints = [waypoints];
            }

            let prevCoords: [number, number]|null = null;
            waypoints.forEach((waypoint: any) => {
                const lon = waypoint.lon;
                const lat = waypoint.lat;
                if (lon === undefined || lat === undefined) {
                    return;
                }
                const coords: [number, number] = [Number.parseFloat(String(lon)), Number.parseFloat(String(lat))];

                const name = waypoint.name ? String(waypoint.name) : '';
                const isPilotPoint = isPilotPointName(name);

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: name,
                        pilotPoint: isPilotPoint ? true : undefined
                    }
                };

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters; // Accept minimal error on dist, assuming GC for RL

                    let isGC = true;
                    const trackType = waypoint.tracktype ? String(waypoint.tracktype) : undefined;
                    if (trackType) {
                        isGC = trackType === 'GreatCircle';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                prevCoords = coords;
            });

            return {
                route: { 
                    routeId: '',
                    route_geometry 
                },
                extras: {
                    // Plan related data.

                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, type: Format.SPOS | Format.SPOS_Pilot, options = {}): string | undefined
    {
        const opt = {
            sposVersion: 8, // TBD (currently, we assume SPOS 8),
            routeName: 'Maersk Voyage Planning Route',
            ...options
        }

        // Bail out if no valid route is selected.
        if (!plan.currentSelectedRouteId)
        {
            throw new Error("No valid route selected in plan!");
        }

        // Only include the selected route.
        const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex

        const route = plan.routes[routeIndex];

        // SPOS 8: includes 'tracktype = RhumbLine | GreatCircle
        const xmlString = '<sposroutetemplate xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlVersion="1" xmlns="http://www.meteogroup-maritime.com/spos/routetemplate"/>';
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const sposroutetemplate = xmlDoc.documentElement;

        const _template = document.createElementNS('http://www.meteogroup-maritime.com/spos/routetemplate', 'template');
        _template.setAttribute('name', opt.routeName);
        if (route.creationTime) {
            _template.setAttribute('created', route.creationTime);
        }
        sposroutetemplate.appendChild(_template);
        
        const _waypoints = document.createElementNS('http://www.meteogroup-maritime.com/spos/routetemplate', 'waypoints');
        _template.appendChild(_waypoints);
        
        const routeGeometry = route.route_geometry;
        
        // Only keep hard points *except* if it is the first waypoint (it can be a navigational point if it is the current position)
        let points = <VoyageRoutePointFeature[]>routeGeometry.filter((item, index) => index === 0 ||
        item.geometry.type === "Point" && item.properties.type === "Hard");

        const legs = <VoyageRouteLineFeature[]>routeGeometry.filter(item => item.geometry.type === "LineString");

        if(type === Format.SPOS_Pilot) {
            const pilotPoints:number[] = [];

            points.forEach((el, idx: number) => {
                if(el.properties.pilotPoint) {
                    pilotPoints.push(idx);
                } 
            }) 

            if( pilotPoints.length < 2 ) {
                throw new Error("Route contains fewer than two Pilot Points");
            } else if(pilotPoints.length >= 2) {
                points = points.filter((point, idx )=> {
                    if(idx >= pilotPoints[0] && idx <= pilotPoints[pilotPoints.length - 1]) return point; 
                })
            }
        }

        for (let i = 0; i < points.length; i++) {
            const point = points[i];
            const wp = document.createElementNS('http://www.meteogroup-maritime.com/spos/routetemplate', 'waypoint');
            
            wp.setAttribute('name', point.properties.name || '');
            wp.setAttribute('lat', "" + point.geometry.coordinates[1]);
            wp.setAttribute('lon', "" + point.geometry.coordinates[0]);
            
            wp.setAttribute('tracktype', this._getExportLegType(legs, points, i));
            
            _waypoints.appendChild(wp);
        }
        
        const xmlSer = new XMLSerializer();
        const result = '<?xml version="1.0"?>\r\n' + xmlSer.serializeToString(xmlDoc);
        
        return result;
    }

    private static _getExportLegType(legs: VoyageRouteLineFeature[], points: VoyageRoutePointFeature[], index: number)
    {
        if (index === 0)
        {
            return "RhumbLine";
        }

        let leg = legForEndPoint(legs, points[index].geometry.coordinates, 1);
        
        if (leg)
        {
            if (leg.geometry.coordinates[0][0] !== points[index-1].geometry.coordinates[0] ||
                leg.geometry.coordinates[0][1] !== points[index-1].geometry.coordinates[1])
            {
                // This leg is from a navigational point (that is now filtered out), use distance instead
                leg = undefined;
            }
        }

        if (!leg)
        {
            const legType = getLegTypeFromDist(points[index-1].geometry.coordinates, points[index].geometry.coordinates);
            return legType === "GreatCircle" ? "GreatCircle" : "RhumbLine";
        }

        return leg.properties.leg_type === "GreatCircle" ? "GreatCircle" : "RhumbLine";
    }
}
